import { useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Loader2 } from 'lucide-react';
import { userService } from "../../../_services";

/**
 * Toggle switch for the "Approve Payment" column in the Admin Users table.
 *
 * The switch is disabled and shows a centered spinner while the API call is in-flight.
 *
 * Props:
 *   numbering        � userId
 *   payment          � current payment approval status (boolean/string)
 *   username         � username (required by GetApprovePayment API)
 *   email            � user email (required by GetApprovePayment API)
 *   tabName          � current active tab name, passed back to setStateOfParent
 *   setStateOfParent � callback(newUserList, tabName) to refresh the parent list
 */
function ToggleSwitch({ numbering, payment, username, email, tabName, setStateOfParent }) {
    const [isChecked, setIsChecked] = useState(!!payment);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleToggle = async (checked) => {
        if (isLoading) return; // prevent double-click while in-flight

        setIsLoading(true);
        setError('');

        // Track what the final checked state should be; default to reverting
        let finalChecked = isChecked;

        try {
            const approvepayment = checked ? "true" : "false";

            const response = await userService.GetApprovePayment(numbering, username, email, approvepayment);

            if (response && response.length > 0) {
                if (response[0].code === "1") {
                    finalChecked = checked; // success � accept the new value

                    // Refresh parent user list to keep table in sync (matches dev-v3 behavior)
                    if (setStateOfParent) {
                        try {
                            const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
                            const [userId, userName] = Usermeta_Data.split(':');
                            const newList = await userService.GetUserList(userId, userName, '-1');
                            if (newList && newList.length > 0) {
                                setStateOfParent(newList, tabName);
                            }
                        } catch (refreshErr) {
                            console.error('Error refreshing user list after toggle:', refreshErr);
                        }
                    }
                } else {
                    setError(response[0].message || 'Oops! Something went wrong. Please try again later.');
                    // finalChecked stays as isChecked (revert)
                }
            } else {
                setError('Oops! Something went wrong. Please try again later.');
                // finalChecked stays as isChecked (revert)
            }
        } catch (err) {
            console.error('Error toggling user payment approval:', err);
            setError('Oops! Something went wrong. Please try again later.');
            // finalChecked stays as isChecked (revert)
        } finally {
            // Update checked state and stop loader together so the
            // switch only moves visually once the loader disappears
            setIsChecked(finalChecked);
            setIsLoading(false);
        }
    };

    return (
        <div className="flex items-center gap-2">
            <div className="relative inline-flex items-center">
                <Switch
                    checked={isChecked}
                    onCheckedChange={handleToggle}
                    disabled={isLoading}
                    className="data-[state=checked]:bg-accent"
                />
                {isLoading && (
                    <Loader2 className="absolute inset-0 m-auto h-4 w-4 animate-spin text-accent pointer-events-none" />
                )}
            </div>
            {error && (
                <span className="text-xs text-destructive">{error}</span>
            )}
        </div>
    );
}

export default ToggleSwitch;

